import React, { useState, useEffect } from 'react';
import TreeNavigator from './tree_navigator';
import EditPanel from './edit_panel';
import './form_editor.css';
import { API_BASE } from '../services/api.js';

const FormTreeEditor = () => {
  // Available options from database
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  
  // Filter states
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);
  
  // Tree data
  const [treeData, setTreeData] = useState({ topics: [], total_nodes: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Current editing
  const [selectedNode, setSelectedNode] = useState(null);
  const [editingNode, setEditingNode] = useState(null);

  // ADD THIS - lift expanded nodes state
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  
  // Success messages
  const [success, setSuccess] = useState(null);

  // API Base URL
  //const API_BASE = `${API_BASE}/admin/tree-editor-form`;

  // Load subjects on component mount
  useEffect(() => {
    loadSubjects();
  }, []);

  // Load chapters when subject changes
  useEffect(() => {
    if (selectedSubject) {
      loadChapters(selectedSubject.subject_id);
      setSelectedChapter(null);
      setSelectedTopic(null);
      setSelectedSubtopic(null);
    } else {
      setChapters([]);
      setSelectedChapter(null);
      setSelectedTopic(null);
      setSelectedSubtopic(null);
    }
  }, [selectedSubject]);

  // Load topics when chapter changes
  useEffect(() => {
    if (selectedChapter) {
      loadTopics(selectedChapter.chapter_id, selectedSubject.subject_id);
      setSelectedTopic(null);
      setSelectedSubtopic(null);
    } else {
      setTopics([]);
      setSelectedTopic(null);
      setSelectedSubtopic(null);
    }
  }, [selectedChapter]);

  // Load subtopics when topic changes
  useEffect(() => {
    if (selectedTopic) {
      loadSubtopics(selectedTopic.topic_id);
      setSelectedSubtopic(null);
    } else {
      setSubtopics([]);
      setSelectedSubtopic(null);
    }
  }, [selectedTopic]);

  // Load chapter tree when chapter is selected
  useEffect(() => {
    if (selectedChapter && selectedSubject) {
      loadChapterTree();
    }
  }, [selectedChapter, selectedSubject]);

  // API Functions
  const loadSubjects = async () => {
    try {
      const response = await fetch(`${API_BASE}/admin/tree-editor-form/subjects`);
      const data = await response.json();
      setSubjects(data);
    } catch (err) {
      setError('Failed to load subjects');
      console.error('Error loading subjects:', err);
    }
  };

  const loadChapters = async (subjectId) => {
    try {
      const response = await fetch(`${API_BASE}/admin/tree-editor-form/chapters?subject_id=${subjectId}`);
      const data = await response.json();
      setChapters(data);
    } catch (err) {
      setError('Failed to load chapters');
      console.error('Error loading chapters:', err);
    }
  };

  const loadTopics = async (chapterId, subjectId) => {
    try {
      const response = await fetch(`${API_BASE}/admin/tree-editor-form/topics?chapter_id=${chapterId}&subject_id=${subjectId}`);
      const data = await response.json();
      setTopics(data);
    } catch (err) {
      setError('Failed to load topics');
      console.error('Error loading topics:', err);
    }
  };

  const loadSubtopics = async (topicId) => {
    try {
      console.log('🔍 Loading subtopics for topic_id:', topicId);
      // Send all required parameters to match backend
      const response = await fetch(`${API_BASE}/admin/tree-editor-form/subtopics?topic_id=${topicId}&chapter_id=${selectedChapter.chapter_id}&subject_id=${selectedSubject.subject_id}`);
      console.log('🔍 Subtopics response status:', response.status);
      
      const data = await response.json();
      console.log('🔍 Subtopics response data:', data);
      console.log('🔍 Is data an array?', Array.isArray(data));
      
      if (Array.isArray(data)) {
        setSubtopics(data);
      } else {
        console.error('❌ Subtopics data is not an array:', data);
        setSubtopics([]);
        setError('Invalid subtopics data format');
      }
    } catch (err) {
      setError('Failed to load subtopics');
      console.error('Error loading subtopics:', err);
      setSubtopics([]);
    }
  };

  const loadChapterTree = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/admin/tree-editor-form/chapter-tree/${selectedChapter.chapter_id}?subject_id=${selectedSubject.subject_id}`);
      const data = await response.json();
      setTreeData(data);
      setError(null);
      return data; // ADD THIS - return the data
    } catch (err) {
      setError('Failed to load chapter tree');
      console.error('Error loading chapter tree:', err);
      return null; // ADD THIS
    } finally {
      setLoading(false);
    }
  };

  // Event Handlers
  const handleNodeSelect = (node) => {
    console.log('Node selected:', node);
    setSelectedNode(node);
    setEditingNode(null); // Exit edit mode when selecting a new node
  };

  const handleEditNode = (node) => {
    console.log('Editing node:', node);
    setEditingNode({ ...node, is_new: false });
  };

  const handleAddChild = (parentNode, childType) => {
    console.log('Adding child:', { parentNode, childType });
    
    let parentContext = {};
    
    if (childType === 'topic') {
      // Topics go under chapters
      parentContext = {
        subject_id: selectedSubject?.subject_id,
        chapter_id: selectedChapter?.chapter_id,
      };
    } else if (childType === 'subtopic') {
      // Subtopics go under topics
      parentContext = {
        subject_id: selectedSubject?.subject_id,
        chapter_id: selectedChapter?.chapter_id,
        topic_id: parentNode.id,
      };
    } else if (childType === 'chunk') {
      // Chunks ALWAYS go under subtopics
      parentContext = {
        subject_id: selectedSubject?.subject_id,
        chapter_id: selectedChapter?.chapter_id,
        topic_id: parentNode.topic_id,
        subtopic_id: parentNode.id,
      };
    } else if (childType === 'micro_chunk') {
      // Micro chunks go under chunks
      parentContext = {
        subject_id: selectedSubject?.subject_id,
        chapter_id: selectedChapter?.chapter_id,
        topic_id: parentNode.topic_id,
        subtopic_id: parentNode.subtopic_id,
        chunk_id: parentNode.id,
      };
    } else if (childType === 'explanation') {
      // Explanations go under micro chunks
      parentContext = {
        subject_id: selectedSubject?.subject_id,
        chapter_id: selectedChapter?.chapter_id,
        topic_id: parentNode.topic_id,
        subtopic_id: parentNode.subtopic_id,
        chunk_id: parentNode.chunk_id,
        micro_chunk_id: parentNode.id,
      };
    }
    
    // Create new node template
    // Calculate next order index based on existing children
    let nextOrderIndex = 1;
    if (parentNode.children && parentNode.children.length > 0) {
      // Find the max order_index among siblings of the same type
      const sameTypeSiblings = parentNode.children.filter(child => child.node_type === childType);
      if (sameTypeSiblings.length > 0) {
        const maxOrder = Math.max(...sameTypeSiblings.map(child => child.order_index || 0));
        nextOrderIndex = maxOrder + 1;
      }
    }

    const newNode = {
      id: null,
      name: '',
      node_type: childType,
      order_index: nextOrderIndex, // Auto-incremented
      is_new: true,
      ...parentContext
    };

    console.log('🎯 New node data:', newNode);
    setEditingNode(newNode);
    setSelectedNode(null);
  };

  const handleSaveNode = async (nodeData, addAnother = false) => {
    console.log('Saving node:', nodeData);
    setLoading(true);
    
    try {
      let response;
      
      if (nodeData.is_new) {
        // Create new node
        response = await fetch(`${API_BASE}/admin/tree-editor-form/node`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(nodeData)
        });
      } else {
        // Update existing node
        response = await fetch(`${API_BASE}/admin/tree-editor-form/node/${nodeData.id}?node_type=${nodeData.node_type}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(nodeData)
        });
      }

      if (response.ok) {
        const result = await response.json();
        console.log('🎯 Backend response:', result);
        
        if (result.success) {
          console.log('✅ Setting success message:', result.message);
          setSuccess(result.message);
          
          const createdNodeId = result.node_id;
          const createdNodeType = nodeData.node_type;
          
          setEditingNode(null);
          
          // Reload tree data
          const updatedTreeData = await loadChapterTree();
          
          // Auto-select the newly created/edited node
          if (createdNodeId && updatedTreeData) {
            const findNodeInTree = (nodes, id, type) => {
              for (const node of nodes) {
                if (node.id === id && node.node_type === type) {
                  return node;
                }
                if (node.children) {
                  const found = findNodeInTree(node.children, id, type);
                  if (found) return found;
                }
              }
              return null;
            };
            
            const nodeToSelect = findNodeInTree(updatedTreeData.topics || [], createdNodeId, createdNodeType);
            if (nodeToSelect) {
              setSelectedNode(nodeToSelect);
              console.log('✅ Auto-selected node:', nodeToSelect);
            }
            // If "Save & Add Another", open new form with same parent context
            if (addAnother && nodeData.is_new) {
              setTimeout(() => {
                // Create new node template with same parent context
                const newNode = {
                  id: null,
                  name: '',
                  node_type: nodeData.node_type,
                  order_index: nodeData.order_index + 1,
                  is_new: true,
                  // Copy parent IDs
                  subject_id: nodeData.subject_id,
                  chapter_id: nodeData.chapter_id,
                  topic_id: nodeData.topic_id,
                  subtopic_id: nodeData.subtopic_id,
                  chunk_id: nodeData.chunk_id,
                  micro_chunk_id: nodeData.micro_chunk_id,
                };
                
                setEditingNode(newNode);
                console.log('✅ Opening new form for sibling node');
              }, 300);
            }
          }
          
          // Clear success message after 2 seconds
          setTimeout(() => setSuccess(null), 2000);
        } else {
          setError(result.message || 'Save failed');
        }
      } else {
        const errorData = await response.json();
        console.log('❌ Backend error:', errorData);
        
        // Handle different error formats
        let errorMessage = 'Failed to save node';
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Validation errors array
            errorMessage = errorData.detail.map(err => err.msg || err).join(', ');
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else {
            errorMessage = JSON.stringify(errorData.detail);
          }
        }
        setError(errorMessage);
      }
    } catch (err) {
      setError('Failed to save node');
      console.error('Error saving node:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNode = async (node) => {
    if (!window.confirm(`Are you sure you want to delete this ${node.node_type}? This will also delete all its children.`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/admin/tree-editor-form/node/${node.id}?node_type=${node.node_type}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const result = await response.json();
        setSuccess(result.message);
        setSelectedNode(null);
        setEditingNode(null);
        
        // Reload tree data
        await loadChapterTree();
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to delete node');
      }
    } catch (err) {
      setError('Failed to delete node');
      console.error('Error deleting node:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    console.log('Cancelling edit');
    setEditingNode(null);
  };

  // Clear messages after 2 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div className="form-tree-editor">
      {/* Header */}
      <div className="editor-header">
        <h1>🌳 Form-Based Content Editor</h1>
        <p>Manage your educational content with an intuitive tree structure</p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          {success}
          <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">❌</span>
          {error}
          <button className="alert-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Selection Panel */}
      <div className="selection-panel">
        <div className="selection-row">
          {/* Subject Selection */}
          <div className="selection-item">
            <label>📚 Subject:</label>
            <select
              value={selectedSubject?.subject_id || ''}
              onChange={(e) => {
                const subject = subjects.find(s => s.subject_id === parseInt(e.target.value));
                setSelectedSubject(subject || null);
              }}
            >
              <option value="">Select Subject</option>
              {subjects.map(subject => (
                <option key={subject.subject_id} value={subject.subject_id}>
                  {subject.name}
                </option>
              ))}
            </select>
          </div>

          {/* Chapter Selection */}
          <div className="selection-item">
            <label>📖 Chapter:</label>
            <select
              value={selectedChapter?.chapter_id || ''}
              onChange={(e) => {
                const chapter = chapters.find(c => c.chapter_id === parseInt(e.target.value));
                setSelectedChapter(chapter || null);
              }}
              disabled={!selectedSubject}
            >
              <option value="">Select Chapter</option>
              {chapters.map(chapter => (
                <option key={chapter.chapter_id} value={chapter.chapter_id}>
                  {chapter.name}
                </option>
              ))}
            </select>
          </div>

          {/* Optional Filters */}
          <div className="selection-item">
            <label>📝 Filter by Topic:</label>
            <select
              value={selectedTopic?.topic_id || ''}
              onChange={(e) => {
                const topic = topics.find(t => t.topic_id === parseInt(e.target.value));
                setSelectedTopic(topic || null);
              }}
              disabled={!selectedChapter}
            >
              <option value="">All Topics</option>
              {topics.map(topic => (
                <option key={topic.topic_id} value={topic.topic_id}>
                  {topic.name}
                </option>
              ))}
            </select>
          </div>

          <div className="selection-item">
            <label>📄 Filter by Subtopic:</label>
            <select
              value={selectedSubtopic?.subtopic_id || ''}
              onChange={(e) => {
                const subtopic = subtopics.find(st => st.subtopic_id === parseInt(e.target.value));
                setSelectedSubtopic(subtopic || null);
              }}
              disabled={!selectedTopic}
            >
              <option value="">All Subtopics</option>
              {subtopics.map(subtopic => (
                <option key={subtopic.subtopic_id} value={subtopic.subtopic_id}>
                  {subtopic.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {!selectedSubject || !selectedChapter ? (
        <div className="empty-state">
          <div className="empty-content">
            <h3>📚 Welcome to Content Management</h3>
            <p>Please select both subject and chapter to start managing content.</p>
          </div>
        </div>
      ) : loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading chapter content...</p>
        </div>
      ) : (
        <div className="editor-main">
          {/* Left Panel - Tree Navigator */}
          <div className="editor-left">
            <TreeNavigator
              treeData={treeData}
              selectedNode={selectedNode}
              onNodeSelect={handleNodeSelect}
              onAddChild={handleAddChild}
              loading={loading}
              selectedTopic={selectedTopic}
              selectedSubtopic={selectedSubtopic}
              expandedNodes={expandedNodes}
              setExpandedNodes={setExpandedNodes}
            />
          </div>

          {/* Right Panel - Edit Form */}
          <div className="editor-right">
            <EditPanel
              selectedNode={selectedNode}
              editingNode={editingNode}
              onEdit={handleEditNode}
              onSave={handleSaveNode}
              onDelete={handleDeleteNode}
              onCancel={handleCancelEdit}
              onAddChild={handleAddChild}
            />
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="editor-footer">
        <div className="footer-stats">
          <span>📊 Total Nodes: {treeData.total_nodes}</span>
          {treeData.loaded_timestamp && (
            <span>🕒 Last Loaded: {new Date(treeData.loaded_timestamp).toLocaleTimeString()}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormTreeEditor;